/**
 * RetirePoolDialog — manual retirement entry point for an Agentic
 * Network pool.
 *
 * Two modes per the lifecycle brief:
 *
 *   * **Retire immediately** — pool flips to ``RETIRED`` now. The
 *     prompt box freezes for the room and operators stop seeing it
 *     in their navigation. Workspace admins can still bring it back
 *     from the Lifecycle tab via Restore.
 *
 *   * **Schedule retirement in N days** — pool flips to ``NOTIFIED``
 *     with ``retireAt = now + N days``. The chat banner + red
 *     warning surface so the creator gets a chance to keep it alive
 *     by sending a prompt. The nightly sweeper auto-advances the
 *     pool to ``RETIRED`` when the deadline elapses; before then the
 *     creator (or any admin) can revoke the retirement by sending a
 *     fresh activity event (auto-resets to ACTIVE on the backend).
 *
 * Wire shape lives in :func:`buildRetireRequestBody` so the dialog
 * cannot send a malformed body — every form-state combination
 * resolves to either a 400-safe payload or an inline validation
 * error before the network round-trip.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import {
  buildRetireRequestBody,
  retirePool,
} from '@/services/poolLifecycleApi';

const REASON_MAX = 140;
const DEFAULT_SCHEDULE_DAYS = 7;

function poolDisplayName(pool) {
  return (
    pool?.poolName?.trim() ||
    pool?.poolDisplayName?.trim() ||
    pool?.poolId ||
    'this room'
  );
}

export default function RetirePoolDialog({
  open,
  networkId,
  pool,
  onClose,
  onRetired,
}) {
  const [mode, setMode] = useState('immediate');
  const [inDays, setInDays] = useState(DEFAULT_SCHEDULE_DAYS);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setMode('immediate');
    setInDays(DEFAULT_SCHEDULE_DAYS);
    setReason('');
    setError(null);
    setSubmitting(false);
    const t = setTimeout(() => cancelRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open, pool?.poolId]);

  const computedBody = useMemo(() => {
    try {
      return {
        body: buildRetireRequestBody({ mode, inDays, reason }),
        validationError: null,
      };
    } catch (e) {
      return { body: null, validationError: e?.message || 'Invalid form' };
    }
  }, [mode, inDays, reason]);

  const submit = async () => {
    if (submitting) return;
    if (!pool?.poolId) {
      setError('Missing pool id');
      return;
    }
    if (!computedBody.body) {
      setError(computedBody.validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await retirePool(networkId, pool.poolId, computedBody.body);
      onRetired?.(response);
    } catch (e) {
      setError(e?.message || 'Failed to retire room');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="retire-pool-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={onClose}
          onKeyDown={onKeyDown}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Retire room"
          >
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <AlertTriangle className="h-4 w-4 text-red-500" aria-hidden />
                Retire {poolDisplayName(pool)}?
              </h3>
              <p className="mt-1 text-xs text-gray-600">
                Retired rooms disappear from operator navigation and
                stop accepting prompts or new runs. Workspace admins can
                restore them from the Lifecycle tab.
              </p>
            </div>

            <fieldset className="space-y-3 px-5 py-4" disabled={submitting}>
              <legend className="sr-only">Retirement mode</legend>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors ${
                  mode === 'immediate'
                    ? 'border-red-200 bg-red-50/60'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="retire-mode"
                  value="immediate"
                  checked={mode === 'immediate'}
                  onChange={() => setMode('immediate')}
                  className="mt-0.5 h-3.5 w-3.5 text-red-600 focus:ring-red-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900">
                    Retire immediately
                  </p>
                  <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
                    The room freezes now. Operators lose access; admins
                    can still restore from the Lifecycle tab.
                  </p>
                </div>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors ${
                  mode === 'schedule'
                    ? 'border-amber-200 bg-amber-50/60'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="retire-mode"
                  value="schedule"
                  checked={mode === 'schedule'}
                  onChange={() => setMode('schedule')}
                  className="mt-0.5 h-3.5 w-3.5 text-amber-600 focus:ring-amber-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900">
                    Schedule retirement
                  </p>
                  <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
                    The room moves to <strong>Notified</strong> and shows
                    a red banner in the chat. Sending a prompt revokes the
                    schedule; otherwise it auto-retires when the deadline
                    elapses.
                  </p>
                  {mode === 'schedule' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[11px] text-gray-700">Retire in</span>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        step={1}
                        value={inDays}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setInDays('');
                            return;
                          }
                          const n = Number(raw);
                          if (Number.isFinite(n)) setInDays(Math.floor(n));
                        }}
                        className="w-16 rounded-md border border-gray-300 px-2 py-1 text-right text-xs text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      />
                      <span className="text-[11px] text-gray-700">
                        {Number(inDays) === 1 ? 'day' : 'days'}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-gray-700">
                  Reason <span className="text-gray-400">(optional)</span>
                </span>
                <textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
                  placeholder="Recorded in the audit log."
                  className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  maxLength={REASON_MAX}
                />
                <span className="mt-1 block text-[10px] text-gray-400">
                  {reason.length}/{REASON_MAX}
                </span>
              </label>

              {error && (
                <p
                  className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700"
                  role="alert"
                >
                  {error}
                </p>
              )}
            </fieldset>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              <button
                ref={cancelRef}
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !computedBody.body}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors disabled:opacity-50 ${
                  mode === 'immediate'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {submitting
                  ? 'Working…'
                  : mode === 'immediate'
                    ? 'Retire now'
                    : `Schedule retirement`}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
